import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { PresenceView } from './voice-call-presence';

/**
 * A one-way channel from the live call — which lives inside `<MessagesPanel>`,
 * because a voice turn is a turn in that conversation — out to the app chrome,
 * which is where the island has to be drawn.
 *
 * It exists because those two are on opposite sides of the tree: the call needs
 * the chat's `ensureConversation`/`appendMessage`, and the island needs to float
 * above every screen INCLUDING a collapsed chat. Hoisting the whole call to
 * `AppFrame` would drag the conversation plumbing with it; publishing a small
 * read-only summary does not.
 *
 * Deliberately NOT a place to put call state: this carries what the island
 * renders plus the controls it offers. The call itself stays where it is.
 *
 * `end` is the one control here that is not presentational — it hangs the call
 * up. It travels because the island offers hanging up WITHOUT expanding first,
 * and the island lives in the chrome while the session lives in the panel. It is
 * the panel's own `voice.end`, not a second teardown path: ending from the
 * island and ending from the call surface are the same call, so the presence
 * machine, the island's exit morph and the transcript flush cannot diverge
 * between the two.
 */
export type VoiceCallSummary = {
  timing: PresenceView;
  muted: boolean;
  working: boolean;
  minimize: () => void;
  expand: () => void;
  end: () => void;
};

type Channel = {
  summary: VoiceCallSummary | null;
  publish: (summary: VoiceCallSummary | null) => void;
};

const VoiceCallPresenceContext = createContext<Channel>({ summary: null, publish: () => {} });

export function VoiceCallPresenceProvider({ children }: { children: ReactNode }) {
  const [summary, setSummary] = useState<VoiceCallSummary | null>(null);
  const value = useMemo<Channel>(() => ({ summary, publish: setSummary }), [summary]);
  return <VoiceCallPresenceContext.Provider value={value}>{children}</VoiceCallPresenceContext.Provider>;
}

/** What the island and the chat chrome read. Null when there is no call. */
export function useVoiceCallSummary(): VoiceCallSummary | null {
  return useContext(VoiceCallPresenceContext).summary;
}

/**
 * Published by the panel that owns the call; cleared when that panel goes away.
 *
 * Takes the fields rather than a ready-made object on purpose: a summary rebuilt
 * every render would publish every render, and publishing is a `setState` on an
 * ancestor — i.e. an infinite loop. Keyed on the values instead, it publishes
 * only when something actually changed.
 */
export function usePublishVoiceCallSummary(
  fields: VoiceCallSummary & { active: boolean },
): void {
  const { publish } = useContext(VoiceCallPresenceContext);
  const { active, timing, muted, working, minimize, expand, end } = fields;
  const { islandVisible, surfaceVisible, elapsedMs, endingSoonMinutes } = timing;
  const summary = useMemo<VoiceCallSummary | null>(
    () =>
      active
        ? {
            timing: { islandVisible, surfaceVisible, elapsedMs, endingSoonMinutes },
            muted,
            working,
            minimize,
            expand,
            end,
          }
        : null,
    [active, islandVisible, surfaceVisible, elapsedMs, endingSoonMinutes, muted, working, minimize, expand, end],
  );
  useEffect(() => {
    publish(summary);
  }, [publish, summary]);
  useEffect(() => () => publish(null), [publish]);
}
